/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';

import { useFormik } from 'formik';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { useUser } from 'hooks';
import { useQuery } from 'utils';

// Components
import { Grid, Link, Mui, MuiLab, PasswordField, TextField, Typography } from '../..';
import { Stack } from '../../layouts';

const useStyles = Mui.makeStyles(theme => ({
  otpContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: theme.spacing(2),
    margin: theme.spacing(1, 0),
    [theme.breakpoints.down('xs')]: {
      gap: theme.spacing(1),
    },
  },
  otpInput: {
    width: 48,
    height: 48,
    '& input': {
      textAlign: 'center',
      padding: 0,
    },
    [theme.breakpoints.down('xs')]: {
      width: 40,
      height: 40,
    },
  },
  errorText: {
    textAlign: 'center',
  },
}));

// Temporary Utils
const secondsUntil = (isoDate, now) => {
  if (!isoDate) return 0;
  const diff = Math.ceil((new Date(isoDate).getTime() - now) / 1000);
  return diff > 0 ? diff : 0;
};

const handleMultipleDigits = (otp, updateOtpValue, inputRefs, digits, startIndex = 0) => {
  const newOtp = [...otp];
  let focusIndex = startIndex;
  digits.split('').forEach((char, idx) => {
    const targetIdx = startIndex + idx;
    if (targetIdx < 6) {
      newOtp[targetIdx] = char;
      focusIndex = targetIdx;
    }
  });
  updateOtpValue(newOtp);
  if (inputRefs.current[focusIndex]) {
    inputRefs.current[focusIndex].focus();
  }
};

const handleChange = (otp, updateOtpValue, inputRefs, index, e) => {
  const cleanVal = e.target.value.replace(/\D/g, '');
  if (cleanVal === otp[index]) {
    e.target.value = cleanVal;
    return;
  }
  const newOtp = [...otp];
  if (cleanVal.length === 0) {
    newOtp[index] = '';
    updateOtpValue(newOtp);
  } else if (cleanVal.length > 1) {
    handleMultipleDigits(otp, updateOtpValue, inputRefs, cleanVal, index);
  } else {
    newOtp[index] = cleanVal;
    updateOtpValue(newOtp);
    if (index < 5 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus();
    }
  }
};

const handleKeyDown = (otp, updateOtpValue, inputRefs, codeFormik, index, e) => {
  if (e.key === 'Backspace') {
    const newOtp = [...otp];
    if (otp[index] !== '') {
      newOtp[index] = '';
      updateOtpValue(newOtp);
    } else if (index > 0) {
      newOtp[index - 1] = '';
      updateOtpValue(newOtp);
      if (inputRefs.current[index - 1]) {
        inputRefs.current[index - 1].focus();
      }
    }
    e.preventDefault();
  } else if (e.key === 'ArrowLeft' && index > 0 && inputRefs.current[index - 1]) {
    inputRefs.current[index - 1].focus();
    e.preventDefault();
  } else if (e.key === 'ArrowRight' && index < 5 && inputRefs.current[index + 1]) {
    inputRefs.current[index + 1].focus();
    e.preventDefault();
  } else if (e.key === 'Enter') {
    codeFormik.handleSubmit();
    e.preventDefault();
  } else if (e.key.length === 1 && !/\d/.test(e.key) && !e.ctrlKey && !e.metaKey) {
    e.preventDefault();
  }
};

const handlePaste = (otp, updateOtpValue, inputRefs, e) => {
  e.preventDefault();
  const clipboardData = e.clipboardData || window.clipboardData;
  const pastedText = clipboardData.getData('text') || '';
  const cleanText = pastedText.replace(/\D/g, '').slice(0, 6);
  if (cleanText.length > 0) {
    handleMultipleDigits(otp, updateOtpValue, inputRefs, cleanText, 0);
  }
};

function Login(props) {
  // Hooks
  const classes = useStyles();
  const query = useQuery();
  const { pathname } = useLocation();
  const {
    twoFactorRequired,
    twoFactorInfo,
    twoFactorError,
    attemptsRemaining,
    confirmTwoFactor,
    resendTwoFactor,
    getTwoFactorStatus,
    resetTwoFactor,
    confirmingTwoFactor,
  } = useUser();

  // Queries
  const redirect = query.get('redirect');

  // Constants
  const { values, onSubmit, SocialAuthentication, loading } = props;

  // States
  const [now, setNow] = useState(() => Date.now());

  const formMessages = {
    email: {
      label: 'E-mail',
      required: 'Ops! Sem o seu e-mail não é possível utilizar nossos serviços',
      invalid: 'Não é um endereço de e-mail válido',
    },
    password: {
      label: 'Senha',
      required: 'Não esqueça sua senha para confirmar que é você',
    },
  };

  const initialValues = {
    email: get(values, 'email', ''),
    password: '',
  };

  const yup = Yup.object().shape({
    email: Yup.string().email(formMessages.email.invalid).required(formMessages.email.required),
    password: Yup.string().required(formMessages.password.required),
  });

  const formik = useFormik({
    initialValues,
    onSubmit: newValues => onSubmit(newValues),
    validationSchema: yup,
    enableReinitialize: true,
    validateOnBlur: false,
  });

  const codeFormik = useFormik({
    initialValues: { code: '', trust: false },
    validationSchema: Yup.object().shape({
      code: Yup.string()
        .matches(/^\d{6}$/, 'Informe os 6 dígitos do código.')
        .required('Informe o código.'),
    }),
    onSubmit: ({ code, trust }) => confirmTwoFactor({ code, trust }),
    validateOnBlur: false,
  });

  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const otpRef = useRef(otp);

  const updateOtpValue = newOtpArray => {
    otpRef.current = newOtpArray;
    setOtp(newOtpArray);
    codeFormik.setFieldValue('code', newOtpArray.join(''));
  };

  const onOtpChange = (index, e) =>
    handleChange(otpRef.current, updateOtpValue, inputRefs, index, e);
  const onOtpKeyDown = (index, e) =>
    handleKeyDown(otpRef.current, updateOtpValue, inputRefs, codeFormik, index, e);
  const onOtpPaste = e => handlePaste(otpRef.current, updateOtpValue, inputRefs, e);

  // Effects
  useEffect(() => {
    getTwoFactorStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!twoFactorRequired) return undefined;
    setNow(Date.now());
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, [twoFactorRequired]);

  useEffect(() => {
    if (!twoFactorRequired) return undefined;
    const timer = setTimeout(() => {
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }, 50);
    return () => clearTimeout(timer);
  }, [twoFactorRequired]);

  // Render
  if (twoFactorRequired) {
    const resendCountdown = secondsUntil(twoFactorInfo?.resendAvailableAt, now);
    const canResend = !twoFactorInfo?.resendAvailableAt || resendCountdown === 0;
    const expired = twoFactorInfo?.expiresAt
      ? secondsUntil(twoFactorInfo.expiresAt, now) === 0
      : false;

    const handleBack = () => {
      codeFormik.resetForm();
      resetTwoFactor();
      setOtp(['', '', '', '', '', '']);
      otpRef.current = ['', '', '', '', '', ''];
    };
    const handleResend = () => {
      codeFormik.resetForm();
      resendTwoFactor();
      setOtp(['', '', '', '', '', '']);
      otpRef.current = ['', '', '', '', '', ''];
    };

    return (
      <Stack>
        <Typography variant="h6" component="p">
          Verificação em duas etapas
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Enviamos um código de verificação para <strong>{twoFactorInfo?.destination}</strong>.
          Informe o código de 6 dígitos para concluir o login.
        </Typography>
        {!!expired && (
          <MuiLab.Alert severity="warning">
            O código expirou. Reenvie para receber um novo código.
          </MuiLab.Alert>
        )}
        {!!twoFactorError && (
          <MuiLab.Alert severity="error">
            {twoFactorError}
            {attemptsRemaining != null && <div>Tentativas restantes: {attemptsRemaining}</div>}
          </MuiLab.Alert>
        )}
        <div className={classes.otpContainer} onPaste={onOtpPaste}>
          <Mui.OutlinedInput
            inputRef={el => {
              inputRefs.current[0] = el;
            }}
            value={otp[0]}
            onChange={e => onOtpChange(0, e)}
            onKeyDown={e => onOtpKeyDown(0, e)}
            onFocus={e => e.target.select()}
            autoFocus
            className={classes.otpInput}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { textAlign: 'center' },
            }}
          />
          <Mui.OutlinedInput
            inputRef={el => {
              inputRefs.current[1] = el;
            }}
            value={otp[1]}
            onChange={e => onOtpChange(1, e)}
            onKeyDown={e => onOtpKeyDown(1, e)}
            onFocus={e => e.target.select()}
            className={classes.otpInput}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { textAlign: 'center' },
            }}
          />
          <Mui.OutlinedInput
            inputRef={el => {
              inputRefs.current[2] = el;
            }}
            value={otp[2]}
            onChange={e => onOtpChange(2, e)}
            onKeyDown={e => onOtpKeyDown(2, e)}
            onFocus={e => e.target.select()}
            className={classes.otpInput}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { textAlign: 'center' },
            }}
          />
          <Mui.OutlinedInput
            inputRef={el => {
              inputRefs.current[3] = el;
            }}
            value={otp[3]}
            onChange={e => onOtpChange(3, e)}
            onKeyDown={e => onOtpKeyDown(3, e)}
            onFocus={e => e.target.select()}
            className={classes.otpInput}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { textAlign: 'center' },
            }}
          />
          <Mui.OutlinedInput
            inputRef={el => {
              inputRefs.current[4] = el;
            }}
            value={otp[4]}
            onChange={e => onOtpChange(4, e)}
            onKeyDown={e => onOtpKeyDown(4, e)}
            onFocus={e => e.target.select()}
            className={classes.otpInput}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { textAlign: 'center' },
            }}
          />
          <Mui.OutlinedInput
            inputRef={el => {
              inputRefs.current[5] = el;
            }}
            value={otp[5]}
            onChange={e => onOtpChange(5, e)}
            onKeyDown={e => onOtpKeyDown(5, e)}
            onFocus={e => e.target.select()}
            className={classes.otpInput}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*',
              style: { textAlign: 'center' },
            }}
          />
        </div>
        {(codeFormik.touched.code || codeFormik.submitCount > 0) && codeFormik.errors.code && (
          <Mui.FormHelperText error className={classes.errorText}>
            {codeFormik.errors.code}
          </Mui.FormHelperText>
        )}
        <Mui.FormControlLabel
          style={{ paddingTop: 0 }}
          control={
            <Mui.Checkbox
              checked={codeFormik.values.trust}
              onChange={e => codeFormik.setFieldValue('trust', e.target.checked)}
              name="trust"
              color="primary"
            />
          }
          label={
            <div>
              <Typography variant="body2">Confiar neste dispositivo</Typography>
              <Typography variant="caption" color="textSecondary">
                Não será necessário verificar novamente neste dispositivo
              </Typography>
            </div>
          }
        />
        <Grid container spacing={2} justifyContent="space-between" alignItems="center">
          <Grid item>
            <Mui.Button variant="text" color="secondary" onClick={handleBack}>
              Voltar
            </Mui.Button>
          </Grid>
          <Grid item>
            <Mui.Button
              onClick={codeFormik.handleSubmit}
              loading={confirmingTwoFactor}
              disabled={expired}>
              Verificar
            </Mui.Button>
          </Grid>
        </Grid>
        <Mui.Divider />
        <Typography variant="body2" component="p" align="center">
          {canResend ? (
            <Link
              component="button"
              type="button"
              variant="body2"
              disabled={confirmingTwoFactor}
              onClick={handleResend}>
              Reenviar código
            </Link>
          ) : (
            <span>Reenviar código disponível em {resendCountdown}s</span>
          )}
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack TransitionComponent={Mui.Fade}>
      {SocialAuthentication}
      <TextField
        autoFocus
        autoComplete="email"
        name="email"
        type="email"
        margin="none"
        formik={formik}
        messages={formMessages}
      />
      <PasswordField
        autoComplete="current-password"
        name="password"
        formik={formik}
        messages={formMessages}
        margin="none"
        onKeyPress={ev => {
          if (ev.key === 'Enter') {
            formik.handleSubmit();
            ev.preventDefault();
          }
        }}
      />
      <Grid container spacing={2} justifyContent="space-between" alignItems="center">
        <Grid item>
          <Link component={RouterLink} to="/password-reset" variant="body2">
            Esqueceu sua senha?
          </Link>
        </Grid>
        <Grid item>
          <Mui.Button onClick={() => formik.handleSubmit()} loading={loading}>
            Entrar
          </Mui.Button>
        </Grid>
      </Grid>
      <Mui.Divider />
      <Typography variant="body2" component="p" align="center">
        Não tem uma conta?{' '}
        <Link
          component={RouterLink}
          variant="body2"
          to={`/sign-up?redirect=${redirect || pathname || ''}`}>
          Cadastre-se
        </Link>
      </Typography>
    </Stack>
  );
}

Login.propTypes = {
  values: PropTypes.object,
  onSubmit: PropTypes.func,
  loading: PropTypes.bool,
  SocialAuthentication: PropTypes.any,
};

export default Login;
